import { useQuery, UseQueryResult } from "@tanstack/react-query";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import saveAs from "file-saver";
import qs from "qs";

const axiosInstance = createAxiosInstance(config.CDR_API_BASE_URL);

export enum CdrExportField {
  MESSAGE_ID = "MESSAGE_ID",
  CLIENT = "CLIENT",
  PROVIDER = "PROVIDER",
  STATUS = "STATUS",
  FAILURE_CAUSE = "FAILURE_CAUSE",
  FALLBACK_CAUSE = "FALLBACK_CAUSE",
  DELIVERY_STAT = "DELIVERY_STAT",
  RATE = "RATE",
  COST = "COST",
  MNP_COST = "MNP_COST",
  HLR_COST = "HLR_COST",
  PREFIX_MATCHED_OPERATOR_ID = "PREFIX_MATCHED_OPERATOR_ID",
  MNP_PROVIDER_ID = "MNP_PROVIDER_ID",
  HLR_PROVIDER_ID = "HLR_PROVIDER_ID",
  ORIGINAL_SOURCE = "ORIGINAL_SOURCE",
  PRE_TRANSLATED_SOURCE = "PRE_TRANSLATED_SOURCE",
  POST_TRANSLATED_SOURCE = "POST_TRANSLATED_SOURCE",
  MESSAGE_TEXT = "MESSAGE_TEXT",
  ORIGINAL_MESSAGE_TEXT = "ORIGINAL_MESSAGE_TEXT",
  CONCATENATED_FULL_TEXT = "CONCATENATED_FULL_TEXT",
  IS_CONCATENATED = "IS_CONCATENATED",
  CONCATENATION_ID = "CONCATENATION_ID",
  CONCATENATION_SIZE = "CONCATENATION_SIZE",
  CONCATENATION_INDEX = "CONCATENATION_INDEX",
  ORIGINAL_DESTINATION = "ORIGINAL_DESTINATION",
  PRE_TRANSLATED_DESTINATION = "PRE_TRANSLATED_DESTINATION",
  POST_TRANSLATED_DESTINATION = "POST_TRANSLATED_DESTINATION",
  COUNTRY = "COUNTRY",
  OPERATOR = "OPERATOR",
  MCC = "MCC",
  MNC = "MNC",
  PROVIDER_MESSAGE_ID = "PROVIDER_MESSAGE_ID",
  IP = "IP",
  TPS = "TPS",
  SOURCE_TON = "SOURCE_TON",
  SOURCE_NPI = "SOURCE_NPI",
  DESTINATION_TON = "DESTINATION_TON",
  DESTINATION_NPI = "DESTINATION_NPI",
  DATE_RECEIVED = "DATE_RECEIVED",
  DATE_SUBMIT_SM_RESPONSE = "DATE_SUBMIT_SM_RESPONSE",
  DATE_STARTED_ROUTING = "DATE_STARTED_ROUTING",
  DATE_ROUTED = "DATE_ROUTED",
  DATE_STARTED_SENDING = "DATE_STARTED_SENDING",
  DATE_SENT = "DATE_SENT",
  DATE_DLR_RECEIVED = "DATE_DLR_RECEIVED",
  DATE_DLR_RECEIVED_FROM_VENDOR = "DATE_DLR_RECEIVED_FROM_VENDOR",
  DELAY_TO_SUBMIT = "DELAY_TO_SUBMIT",
  DELAY_DLR = "DELAY_DLR",
  STAGE = "STAGE",
  CLIENT_INTERFACE = "CLIENT_INTERFACE",
  VENDOR_INTERFACE = "VENDOR_INTERFACE",
  RETRY_COUNT = "RETRY_COUNT",
}

export enum CdrStage {
  RECEIVED,
  ROUTED,
  SENT,
  UNROUTED,
  DLR_RECEIVED,
  DLR_SENT,
}

export enum CdrStatus {
  PENDING,
  DONE,
  ERROR,
  FAILED,
  EXPIRED,
  CLIENT_NOT_RESPONDING,
  PENDING_CLIENT_DLR,
  CLIENT_REJECTED,
}
export enum DeliveryReceiptState {
  ENROUTE,
  DELIVRD,
  EXPIRED,
  DELETED,
  UNDELIVERABLE,
  ACCEPTED,
  REJECTED,
}

export interface MessageTrackerType {
  id: number;
  messageId: string;
  messageText: string;
  lastModified: string;
  dateReceived: string;
  dateSent: string;
  dateStartedSending: string;
  dateSubmitSmResponse: string;
  terminatorId: number;
  connectionProfileId: number;
  originalSource: string;
  preTranslatedSource: string;
  postTranslatedSource: string;
  originalDestination: string;
  preTranslatedDestination: string;
  postTranslatedDestination: string;
  terminatorMessageId: string;
  deliveryStat: string;
  status: CdrStatus;
  stage: CdrStage;
  sourceOperatorId: number;
  destinationOperatorId: number;
  vendorId: number;
  fallbackOfVendorId?: number;
  ip: string;
  sessionId: string;
  originalMessageText: string;
  dataCoding: string;
  clientProtocol: string;
  vendorProtocol: string;
  esmClass: string;
  isConcatenated: boolean;
  concatenatedFullText: string;
  partNumber: number;
  rate: number;
  routingCost: number;
  ratePerDelivery: boolean;
  costPerDelivery: boolean;
  dateDlrRespReceivedFromClient?: Date;
  dateDlrStartedSendingToClient?: Date;
  dateDlrReceivedFromVendor: Date;
  tps: number;
  countryId: string;
  failureCause?: string;
  mnpCost: number;
  hlrCost: number;
  prefixMatchedOperatorId: number;
  mnpProviderId: number;
  hlrProviderId: number;
  mnpResponseCode: number;
  hlrResponseCode: number;
  convertedRoutingCost?: number;
  convertedMnpCost?: number;
  convertedHlrCost?: number;
}
export async function fetchMessageTrackers(
  params: Params<MessageTrackerType>
): Promise<IPageable<MessageTrackerType>> {
  try {
    const finalParams: any = { ...params };
    let fromDate = (params as any).fromDate;
    let toDate = (params as any).toDate;
    if (
      !("fromDate" in params) ||
      !("toDate" in params) ||
      !fromDate ||
      !toDate
    ) {
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const dd = String(now.getDate()).padStart(2, "0");
      const todayStr = `${yyyy}-${mm}-${dd}`;
      const tomorrow = new Date(now);
      tomorrow.setDate(now.getDate() + 1);
      const yyyy2 = tomorrow.getFullYear();
      const mm2 = String(tomorrow.getMonth() + 1).padStart(2, "0");
      const dd2 = String(tomorrow.getDate()).padStart(2, "0");
      const tomorrowStr = `${yyyy2}-${mm2}-${dd2}`;
      finalParams.fromDate = new Date(todayStr);
      finalParams.toDate = new Date(tomorrowStr);
    }

    // Always map countryId to destinationCountryId if provided
    if (params.countryId) {
      finalParams.destinationCountryId = params.countryId;
    }

    finalParams.sort = params.sort || "dateReceived,desc";
    const response = await axiosInstance.get("/cdrs", {
      params: finalParams,
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: "repeat", encode: false }),
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the cdrs."
    );
  }
}
export async function fetchMessageTrackersDialog(
  params: Params<MessageTrackerType>
): Promise<IPageable<MessageTrackerType>> {
  try {
    const finalParams: any = { ...params };
    const fromDate = (params as any).fromDate as
      | Date
      | string
      | undefined
      | null;
    const toDate = (params as any).toDate as Date | string | undefined | null;
    if (!fromDate || !toDate) {
      delete finalParams.fromDate;
      delete finalParams.toDate;
    } else {
      if (fromDate instanceof Date) {
        finalParams.fromDate = fromDate.toISOString();
      }
      if (toDate instanceof Date) {
        finalParams.toDate = toDate.toISOString();
      }
    }

    if (params.countryId) {
      finalParams.destinationCountryId = params.countryId;
    }

    finalParams.sort = params.sort || "dateReceived,desc";
    const response = await axiosInstance.get("/cdrs", {
      params: finalParams,
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: "repeat", encode: false }),
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the cdrs."
    );
  }
}

export const usefetchMessageTrackersQuery = (
  params: Params<MessageTrackerType>,
  options?: { enabled?: boolean }
): UseQueryResult<IPageable<MessageTrackerType>, Error> => {
  const queryKey = ["messageTrackers", JSON.stringify(params)];
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useQuery({
    queryKey,
    queryFn: () => fetchMessageTrackers(params),
    enabled: options?.enabled ?? true,
  });
};

export const exportMessageTracker = async (
  params: Params<MessageTrackerType> = {}
): Promise<void> => {
  try {
    const response = await axiosInstance.get("/cdrs/csv", {
      params,
      responseType: "blob",
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: "repeat", encode: false }),
    });

    const disposition = response.headers["content-disposition"];
    let filename = "MessageTracker.csv";
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, filename);

    console.log("Export successful and file downloaded.");
  } catch (error: any) {
    console.log("errorMessageTrackerService", error);

    let apiMessage = "Export failed. Please try again.";

    try {
      if (error.response?.data instanceof Blob) {
        const errorData = JSON.parse(await error.response.data.text());
        console.log("Blob text content:", errorData);

        if (errorData?.message) {
          const colonIndex = errorData.message.indexOf(" : ");
          apiMessage =
            colonIndex !== -1
              ? (() => {
                  try {
                    return (
                      JSON.parse(
                        errorData.message
                          .substring(colonIndex + 3)
                          .replace(/^"|"$/g, "")
                          .replace(/\\"/g, '"')
                      )?.message || errorData.message.substring(colonIndex + 3)
                    );
                  } catch {
                    return errorData.message.substring(colonIndex + 3);
                  }
                })()
              : errorData.message;
        }

        console.log("Parsed error data:", errorData);
        console.log("Extracted nested message:", apiMessage);
      }
    } catch {
      apiMessage =
        (error.response?.data as any)?.message?.message ||
        (error as any).message?.message ||
        (error.response?.data as any)?.message ||
        "Export failed. Please try again.";
    }

    apiMessage =
      typeof apiMessage === "object"
        ? (apiMessage as any)?.message || apiMessage
        : apiMessage;

    console.log("Final error message:", apiMessage);
    throw new Error(apiMessage);
  }
};

export interface RepriceParams extends Params<MessageTrackerType> {
  newRate?: number;
  newCost?: number;
  fromDate?: Date | string;
  toDate?: Date | string;
}

export enum RepricingSessionStatus {
  RUNNING = "RUNNING",
  DONE = "DONE",
  ERROR = "ERROR",
}

export interface IRepricingSession {
  id: number;
  fromDate: string;
  toDate: string;
  connectionProfileId: number;
  destinationCountryId: number;
  destinationOperatorId: number;
  vendorId: number;
  status: string;
  dlrStatus: string;
  postTranslatedSource: string;
  search: string;
  criteria: {
    postTranslatedSourceEquals?: string[];
    originalSourceEquals?: string[];
    postTranslatedDestinationEquals?: string[];
    terminatorMessageIdEquals?: string[];
    messageIdEquals?: string[];
    messageTextEquals?: string[];
    originalMessageTextEquals?: string[];
    postTranslatedSourceContains?: string[];
    originalSourceContains?: string[];
    postTranslatedDestinationContains?: string[];
    terminatorMessageIdContains?: string[];
    messageIdContains?: string[];
    messageTextContains?: string[];
    originalMessageTextContains?: string[];
    filterForNullVendorId?: boolean;
  };
  newRate: number;
  newCost: number;
  repricingStatus: RepricingSessionStatus;
  updatedCount: number;
  errorMessage: string;
  createdBy: string;
  createdDate: string;
  version: number;
}

export async function fetchRepricingSessions(
  params: Params<IRepricingSession> = {}
): Promise<IPageable<IRepricingSession>> {
  try {
    const finalParams = {
      ...params,
      sort: params.sort || "createdDate,desc",
    };
    const response = await axiosInstance.get("/repricing-sessions", {
      params: finalParams,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching repricing sessions."
    );
  }
}

export const useRepricingSessionsQuery = (
  params: Params<IRepricingSession> = {}
): UseQueryResult<IPageable<IRepricingSession>, Error> => {
  const queryKey = ["repricingSessions", JSON.stringify(params)];
  return useQuery({
    queryKey,
    queryFn: () => fetchRepricingSessions(params),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 2000,
  });
};

export const repriceCdrs = async (params: RepriceParams): Promise<number> => {
  try {
    const finalParams: any = { ...params };

    // Handle date conversion
    if (params.fromDate instanceof Date) {
      finalParams.fromDate = params.fromDate.toISOString();
    }
    if (params.toDate instanceof Date) {
      finalParams.toDate = params.toDate.toISOString();
    }

    // Map countryId to destinationCountryId if provided
    if (params.countryId) {
      finalParams.destinationCountryId = params.countryId;
    }

    const response = await axiosInstance.post("/cdrs/reprice", null, {
      params: finalParams,
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: "repeat", encode: false }),
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while repricing the cdrs."
    );
  }
};
